import {
  checkoutV202604,
  type operations,
} from "@fluid-app/checkout-api-client";

import { fetchApi } from "@/lib/api-helpers";
import { createCheckoutApiClient } from "@/lib/checkout-api-client";
import { bundledItemSchema, cartResponseSchema } from "@/networking/carts.api";
import { z } from "zod";

const subscriptionResponseSchema = z.object({
  subscriptions: z.array(
    z.object({
      id: z.number(),
      subscription_token: z.string(),
      created_at: z.string(),
      customer: z.object({
        id: z.number(),
        account_id: z.string(),
        active: z.boolean(),
        default_address: z
          .object({
            id: z.number(),
            address1: z.string().nullable(),
            address2: z.string().nullable().optional(),
            city: z.string().nullable(),
            state: z.string().nullable(),
            postal_code: z.string().nullable(),
            country_code: z.string().nullable(),
          })
          .nullable()
          .optional(),
        display_total_spent: z.string(),
        email: z.string(),
        first_name: z.string(),
        full_name: z.string(),
        is_rep: z.boolean(),
        last_name: z.string(),
        orders_count: z.number(),
        phone: z.string().nullable(),
        total_spent: z.string(),
        user_company_id: z.number().nullable(),
      }),
      last_bill_date: z.string().nullable(),
      next_bill_date: z.string().nullable(),
      next_ship_date: z.string().nullable().optional(),
      price: z.number(),
      quantity: z.number(),
      status: z.string(),
      timezone: z.string().nullable().optional(),
      subscription_plan: z.object({
        id: z.number(),
        active: z.boolean(),
        name: z.string().optional(),
        billing_interval: z.number(),
        billing_interval_unit: z.string(),
        shipping_interval: z.number(),
        shipping_interval_unit: z.string(),
      }),
      address_id: z.number().nullable().optional(),
      variant: z
        .object({
          id: z.number(),
          primary_image: z.string().nullable().optional(),
          product: z.object({
            id: z.number(),
            image_url: z.string(),
            price_in_currency: z.string(),
            title: z.string(),
          }),
          title: z.string(),
          display_name: z.string().nullable().optional(),
        })
        .nullable(),
    }),
  ),
  meta: z.object({
    request_id: z.string(),
    timestamp: z.string(),
    pagination: z
      .object({
        current_page: z.number(),
        next_page: z.number().optional(),
        prev_page: z.number().optional(),
        total_count: z.number(),
        total_pages: z.number(),
        per_page: z.number(),
      })
      .optional(),
  }),
});

export const subscriptionDiscountSchema = z.object({
  id: z.number(),
  duration: z.string(),
  duration_cycles: z.number().nullable(),
  applied_cycles: z.number(),
  removed_reason: z.string().nullable(),
  attached_via: z.string(),
  discount: z.object({ id: z.number(), code: z.string() }),
  discount_version: z.object({
    id: z.number(),
    version_number: z.number(),
    code: z.string(),
    price_discount_type: z.string(),
    flat_value: z.number().nullable(),
    percentage_value: z.number().nullable(),
    apply_to_volume: z.boolean(),
  }),
  starts_at: z.string().nullable(),
  removed_at: z.string().nullable(),
  attached_by: z
    .object({
      kind: z.string(),
      id: z.union([z.number(), z.string()]),
      label: z.string().nullable(),
    })
    .nullable(),
});

export type SubscriptionDiscount = z.infer<typeof subscriptionDiscountSchema>;

const subscriptionDetailSchema = z.object({
  subscription: z.object({
    id: z.number(),
    next_order: z
      .object({
        bill_date: z.string(),
        item_quantity: z.string(),
        item_price: z.string(),
      })
      .nullable()
      .optional(),
    subscription_token: z.string(),
    next_bill_date: z.string().nullable(),
    last_bill_date: z.string().nullable(),
    next_ship_date: z.string().nullable(),
    last_ship_date: z.string().nullable(),
    status: z.string(),
    timezone: z.string().nullable().optional(),
    disabled: z.boolean().nullable().default(false),
    digital: z.boolean(),
    quantity: z.number().min(1).default(1),
    original_price: z.number(),
    price: z.number(),
    shipping_fee: z.number().nullable().optional(),
    shipping_fee_for_display: z.string().nullable().optional(),
    attempts: z.number().nullable().default(0),
    decline_count: z.number().nullable().default(0),
    last_failed_at: z.string().nullable(),
    next_retry_at: z.string().nullable(),
    skipped_count: z.number().nullable().default(0),
    max_skips: z.number().nullable(),
    trial_ends_at: z.string().nullish(),
    cancelled_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    notes: z.string().nullable(),
    in_trial: z.boolean().nullish(),
    bundled_items: z.array(bundledItemSchema).optional(),
    bundled_subscriptions: z
      .array(
        z.object({
          subscription_token: z.string(),
          product_title: z.string(),
          variant_title: z.string().nullable(),
          price: z.number(),
          image_url: z.string().nullable(),
        }),
      )
      .optional()
      .default([]),
    subscription_plan: z.object({
      id: z.number(),
      name: z.string(),
      billing_interval: z.number().min(1).default(1),
      billing_interval_unit: z.string().default("month"),
      billing_frequency_in_words: z.string(),
      shipping_interval: z.number().min(1).default(1),
      shipping_interval_unit: z.string().default("month"),
      trial_period: z.number().min(1).nullable(),
      trial_period_unit: z.string().nullable(),
      split_volume: z.boolean().default(false),
      allow_skipping: z.boolean().nullable().default(true),
      max_skips: z.number().min(1).nullable(),
      price_adjustment_type: z.string().nullable(),
      price_adjustment_amount: z.number().nullable(),
      max_quantity: z.number().min(1).nullable().default(null),
      active: z.boolean().nullable().default(true),
      company_default: z.boolean().nullable().default(false),
      created_at: z.string(),
      updated_at: z.string(),
      products_count: z.number().nullable(),
      subscribers_count: z.number().nullable(),
    }),

    customer: z.object({
      id: z.number(),
      account_id: z.string().nullable(),
      first_name: z.string().nullable(),
      last_name: z.string().nullable(),
      full_name: z.string(),
      email: z.string(),
      verified_email: z.boolean().nullable().default(false),
      phone: z.string().nullable(),
      orders_count: z.number().nullable().default(0),
      created_at: z.string(),
      display_total_spent: z.string(),
      metadata: z.object({}).default({}),
      user_company_id: z.number().nullable(),
      external_id: z.string().nullable(),
      last_order_id: z.number().nullable(),
      notes: z.string().nullable(),
      active: z.boolean(),
      total_spent: z.union([z.number(), z.string()]),
      customer_notes: z.array(
        z.object({
          id: z.number(),
          note: z.string(),
          user_company_id: z.number().nullable(),
          customer_id: z.number(),
          resource_id: z.number().nullable(),
          resource_type: z.string().nullable(),
          created_at: z.string(),
        }),
      ),
      default_address: z
        .object({
          id: z.number(),
          name: z.string(),
          address1: z.string(),
          address2: z.string().nullable(),
          address3: z.string().nullable(),
          city: z.string(),
          state: z.string(),
          subdivision_code: z.string().nullable(),
          postal_code: z.string(),
          country_code: z.string().nullable(),
          default: z.boolean(),
        })
        .nullable(),
      addresses: z.array(
        z.object({
          id: z.number(),
          name: z.string().nullable(),
          address1: z.string().nullable(),
          address2: z.string().nullable(),
          address3: z.string().nullable(),
          city: z.string().nullable(),
          state: z.string().nullable(),
          subdivision_code: z.string().nullable(),
          postal_code: z.string().nullable(),
          country_code: z.string().nullable(),
          default: z.boolean().nullable(),
        }),
      ),
      active_subscriptions_count: z.number().optional(),
      inactive_subscriptions_count: z.number().optional(),
      is_rep: z.boolean(),
      jwt: z.string().optional(),
    }),

    variant: z
      .object({
        id: z.number(),
        title: z.string().nullable(),
        display_name: z.string().nullable().optional(),
        image_url: z.string().nullable(),
        image_path: z.string().nullable(),
        sku: z.string().nullable(),
        primary_image: z.string().nullable(),
        price: z.coerce.number().nullable(),
        price_in_currency: z.string(),
        options: z.array(
          z.object({
            title: z.string(),
            value: z.string(),
          }),
        ),
        product: z.object({
          id: z.number(),
          title: z.string(),
          image_url: z.string().nullable(),
          image_path: z.string().nullable(),
          price: z.union([z.number(), z.string()]).nullable().default("0.0"),
          cv: z.number().nullable().default(0),
          tax: z.union([z.number(), z.string()]).nullable().default("0.0"),
          sku: z.string().nullable(),
          price_in_currency: z.string(),
          tax_in_currency: z.string(),
          cv_in_currency: z.string(),
        }),
      })
      .nullable(),

    address: z.object({
      id: z.number().nullable(),
      name: z.string().nullable(),
      address1: z.string(),
      address2: z.string().nullable(),
      address3: z.string().nullable().optional(),
      city: z.string(),
      state: z.string(),
      subdivision_code: z.string().nullable(),
      postal_code: z.string(),
      country_code: z.string().nullable(),
      default: z.boolean().default(false),
    }),

    payment_method: z.object({
      id: z.number(),
      source: z.string(),
      created_at: z.string(),
      updated_at: z.string(),
      default: z.boolean(),
      payment_type: z.string(),
      details: z.object({}),
    }),

    currency: z.object({
      id: z.number(),
      name: z.string().nullable(),
      code: z.string(),
      rate: z.union([z.number(), z.string()]).nullable(),
      active: z.boolean().nullable().default(false),
      symbol: z.string().nullable(),
    }),

    country: z.object({
      id: z.number(),
      name: z.string(),
      iso: z.string(),
    }),

    company: z.object({
      id: z.number(),
      name: z.string(),
    }),

    subscription_skips: z.array(
      z.object({
        id: z.number(),
        skipped_date: z.string(),
        reason: z.string().nullable(),
        unskipped_at: z.string().nullable().optional(),
        created_at: z.string(),
        updated_at: z.string(),
      }),
    ),

    orders: z.array(
      z.object({
        id: z.number(),
        first_name: z.string().nullable(),
        last_name: z.string().nullable(),
        email: z.string().nullable(),
        phone: z.string().nullable(),
        order_number: z.string().nullable(),
        status: z.string(),
        order_type: z.string(),
        amount: z.union([z.number(), z.string()]).nullable().default("0.0"),
        note: z.string().nullable(),
        currency_code: z.string().nullable(),
        created_at: z.string(),
        updated_at: z.string(),
        metadata: z.object({}).nullable().default({}),
        token: z.string(),
        refundable_amount: z.string(),
        items_quantity: z.number(),
      }),
    ),

    subscription_discounts: z
      .array(subscriptionDiscountSchema)
      .optional()
      .default([]),
    renewal_discount_total: z.number().optional().default(0),
  }),

  meta: z
    .object({
      request_id: z.string(),
      timestamp: z.string(),
    })
    .optional(),
});

export type SubscriptionListItem = z.infer<
  typeof subscriptionResponseSchema
>["subscriptions"][number];

export type SubscriptionDetail = z.infer<typeof subscriptionDetailSchema>;

export async function updateSubscription(
  cartToken: string,
  itemId: number,
  subscription: boolean,
  subscriptionPlanId?: number,
) {
  const query = new URLSearchParams({
    subscription_plan_id: subscriptionPlanId?.toString() ?? "",
  }).toString();
  return fetchApi({
    endpoint: `/carts/${cartToken}/items/${itemId}/subscribe?${query}`,
    method: subscription ? "POST" : "DELETE",
    outputSchema: cartResponseSchema,
  });
}

export async function fetchCustomerSubscriptions(
  customerId: number | undefined,
  page: number = 1,
  pageSize: number = 10,
  jwt: string,
  status: string | null = null,
  search: string | null = null,
  sortBy: string | null = null,
  sortDirection: string | null = null,
) {
  if (!customerId) {
    throw new Error("Customer ID is required to fetch subscriptions");
  }

  return fetchApi({
    endpoint: `/subscriptions`,
    method: "GET",
    input: {
      customer_id: customerId,
      page: page.toString(),
      per_page: pageSize.toString(),
      ...(status !== null && { status }),
      ...(search !== null && { search }),
      ...(sortBy !== null && { sort_by: sortBy }),
      ...(sortDirection !== null && { sort_direction: sortDirection }),
    },
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    outputSchema: subscriptionResponseSchema,
  });
}

export async function fetchSubscription(
  subscriptionToken: string,
  jwt: string,
) {
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}`,
    method: "GET",
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export async function pauseSubscription(
  subscriptionToken: string,
  jwt: string,
  customerId: number,
  nextBillDate: string | null,
  numberOfOrders?: number | null,
) {
  const queryParams: Record<string, string> = {
    customer_id: customerId.toString(),
  };

  // only add next_bill_date if it's not null (for indefinite pause)
  if (nextBillDate !== null) {
    queryParams.next_bill_date = nextBillDate;
  }

  // only add number_of_orders if it's provided
  if (numberOfOrders !== null && numberOfOrders !== undefined) {
    queryParams.number_of_orders = numberOfOrders.toString();
  }

  const query = new URLSearchParams(queryParams).toString();

  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}/pause?${query}`,
    method: "POST",
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export async function resumeSubscription(
  subscriptionToken: string,
  jwt: string,
  customerId: number,
  nextBillDate: string,
) {
  const query = new URLSearchParams({
    customer_id: customerId.toString(),
    next_bill_date: nextBillDate,
  }).toString();
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}/resume?${query}`,
    method: "POST",
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export async function skipSubscription(
  subscriptionToken: string,
  jwt: string,
  customerId: number,
  nextBillDate: string,
) {
  const query = new URLSearchParams({
    customer_id: customerId.toString(),
    next_bill_date: nextBillDate,
  }).toString();
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}/skip_next_billing?${query}`,
    method: "POST",
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export async function cancelSubscription(
  subscriptionToken: string,
  jwt: string,
  customerId: number,
) {
  const query = new URLSearchParams({
    customer_id: customerId.toString(),
  }).toString();
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}/cancel?${query}`,
    method: "POST",
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

// reverts pending skip without using skip allowance.
export async function undoSkipSubscription(
  subscriptionToken: string,
  jwt: string,
  customerId: number,
) {
  const query = new URLSearchParams({
    customer_id: customerId.toString(),
  }).toString();
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}/undo_skip?${query}`,
    method: "POST",
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
  });
}

export type ReactivateSubscriptionParams = {
  next_bill_date?: string;
  process_immediately?: boolean;
};

export async function reactivateSubscription(
  subscriptionToken: string,
  jwt: string,
  params: ReactivateSubscriptionParams,
) {
  const body =
    params.process_immediately === true
      ? { process_immediately: true }
      : params.next_bill_date
        ? { next_bill_date: params.next_bill_date }
        : {};
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}/reactivate`,
    method: "POST",
    input: Object.keys(body).length > 0 ? body : undefined,
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
        "x-fluid-client": "checkout",
      },
    },
  });
}

// Explicit retry on a past-due subscription. Called from the management
// UI after a successful payment-method update so retry is opt-in (vs.
// a side-effect of the update endpoint). The backend gates on past_due
// — non-past-due subs return 200 with no state change, so it's safe to
// call unconditionally after a payment-method update.
export async function triggerSubscriptionRetry(
  subscriptionToken: string,
  jwt: string,
) {
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}/retry`,
    method: "POST",
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
        "x-fluid-client": "checkout",
      },
    },
  });
}

export async function updateSubscriptionInfo({
  subscriptionToken,
  body,
  jwt,
}: {
  subscriptionToken: string;
  body: {
    subscription_plan_id: number;
    customer_id: number;
    variant_id: number;
    address_id: number;
    payment_method_id: number;
    quantity?: number;
    next_bill_date?: string;
  };
  jwt: string;
}) {
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}`,
    method: "PUT",
    input: { subscription: body },
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
        "x-fluid-client": "checkout",
      },
    },
  });
}

export async function updateSubscriptionWithDetail({
  subscriptionToken,
  updates,
  jwt,
}: {
  subscriptionToken: string;
  updates: {
    address_id?: number;
    next_bill_date?: string;
    payment_method_id?: number;
  };
  jwt: string;
}) {
  const detail = await fetchSubscription(subscriptionToken, jwt);
  const sub = detail.subscription;
  if (!sub.variant) {
    throw new Error(
      `Cannot update subscription ${subscriptionToken}: variant is missing on the server.`,
    );
  }
  return fetchApi({
    endpoint: `/subscriptions/${subscriptionToken}`,
    method: "PUT",
    input: {
      subscription: {
        subscription_plan_id: sub.subscription_plan.id,
        customer_id: sub.customer.id,
        variant_id: sub.variant.id,
        address_id: updates.address_id ?? sub.address.id,
        payment_method_id: updates.payment_method_id ?? sub.payment_method.id,
        ...(updates.next_bill_date
          ? { next_bill_date: updates.next_bill_date }
          : {}),
      },
    },
    outputSchema: subscriptionDetailSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
        "x-fluid-client": "checkout",
      },
    },
  });
}

export type SubscriptionBundlesResponse =
  operations["checkout_v2026_04_list_subscription_bundles"]["responses"][200]["content"]["application/json"];

export type SubscriptionBundle =
  SubscriptionBundlesResponse["subscription_bundles"][number];

export type SubscriptionBundleItem =
  SubscriptionBundle["subscriptions"][number];

type CreateSubscriptionBundleBody = NonNullable<
  operations["checkout_v2026_04_create_subscription_bundle"]["requestBody"]
>["content"]["application/json"];

export async function fetchSubscriptionBundles(
  jwt: string,
  customerId?: number,
): Promise<SubscriptionBundlesResponse> {
  return checkoutV202604.checkout_v2026_04_list_subscription_bundles(
    createCheckoutApiClient(jwt),
    customerId == null ? undefined : { customer_id: customerId },
  );
}

export async function bundleSubscriptions({
  subscriptionTokens,
  addressId,
  nextBillDate,
  volumeRepId,
  jwt,
}: {
  subscriptionTokens: string[];
  addressId: number;
  nextBillDate: string;
  volumeRepId?: number;
  jwt: string;
}) {
  const body = {
    bundle: {
      subscription_tokens: subscriptionTokens,
      address_id: addressId,
      next_bill_date: nextBillDate,
      ...(volumeRepId != null ? { volume_rep_id: volumeRepId } : {}),
    },
  } satisfies CreateSubscriptionBundleBody;

  return checkoutV202604.checkout_v2026_04_create_subscription_bundle(
    createCheckoutApiClient(jwt),
    body,
  );
}

// Schema for cart creation
const cartCreateSchema = z.object({
  country_code: z.string(),
  items: z
    .array(
      z.object({
        variant_id: z.number(),
        quantity: z.number(),
        subscription: z.boolean().optional(),
        subscription_plan_id: z.number().optional(),
      }),
    )
    .optional(),
});

const cartCreateResponseSchema = z.object({
  cart: z.object({
    cart_token: z.string(),
    // Add other cart properties as needed
  }),
  meta: z.object({
    request_id: z.string(),
    timestamp: z.string(),
  }),
});

// Function to create a cart
export async function createCart(
  countryCode: string,
  items: Array<{
    variant_id: number;
    quantity: number;
    subscription?: boolean;
    subscription_plan_id?: number;
  }> = [],
  jwt?: string,
) {
  return fetchApi({
    endpoint: `/carts`,
    method: "POST",
    input: {
      country_code: countryCode,
      items: items,
    },
    inputSchema: cartCreateSchema,
    outputSchema: cartCreateResponseSchema,
    fetchOptions: {
      headers: {
        ...(jwt && { Authorization: `Bearer ${jwt}` }),
        "Content-Type": "application/json",
      },
    },
  });
}

// Schema for subscription creation
const subscriptionCreateSchema = z.object({
  subscription: z.object({
    subscription_plan_id: z.number(),
    customer_id: z.number(),
    variant_id: z.number(),
    address_id: z.number(),
    payment_method_id: z.number(),
    cart_token: z.string(),
    quantity: z.number().optional(),
    status: z.string().optional(),
    notes: z.string().optional(),
    next_bill_date: z.string().optional(),
  }),
});

const subscriptionCreateResponseSchema = z.object({
  subscription: z.object({
    id: z.number(),
    subscription_token: z.string(),
    // Add other subscription properties as needed
  }),
  meta: z.object({
    request_id: z.string(),
    timestamp: z.string(),
  }),
});

// Function to create a subscription
export async function createSubscription(
  subscriptionData: {
    subscription_plan_id: number;
    customer_id: number;
    variant_id: number;
    address_id: number;
    payment_method_id: number;
    quantity?: number;
    status?: string;
    notes?: string;
    next_bill_date?: string;
  },
  jwt: string,
) {
  // First, create a cart
  const cartResponse = await createCart(
    "US",
    [
      {
        variant_id: subscriptionData.variant_id,
        quantity: subscriptionData.quantity || 1,
        subscription: true,
        subscription_plan_id: subscriptionData.subscription_plan_id,
      },
    ],
    jwt,
  );

  // Then, create the subscription using the cart token
  return fetchApi({
    endpoint: `/subscriptions`,
    method: "POST",
    input: {
      subscription: {
        ...subscriptionData,
        cart_token: cartResponse.cart.cart_token,
      },
    },
    inputSchema: subscriptionCreateSchema,
    outputSchema: subscriptionCreateResponseSchema,
    fetchOptions: {
      headers: {
        Authorization: `Bearer ${jwt}`,
        "Content-Type": "application/json",
        "x-fluid-client": "checkout",
      },
    },
  });
}
